import type { ApprovedGithubArchiveSource, SourceArchiveCreated, SourceArchiveAdmission } from "./sourceArchiveTypes";
import type { FormationJobSnapshot } from "./formationJob";
/**
 * Personal Apps v0 control-plane client.
 *
 * Deliberately thin: the interesting logic lives on the server, because the
 * artifact and the instance's state are things a browser must not be able to
 * name for itself.
 */

import { accountApiBase, fetchWithConnectionAuth, withConnectionAuth } from "@/app/connection";
import { fetchWithAuthRecovery } from "@/auth/authRecovery";
import type {
  AppSourcePresentation,
  DataPreview,
  DiscoverPublication,
  DownloadSourcePresentation,
  ExecutionOffer,
  ExecutionPlacement,
  ExecutionProvider,
  InstancePresence,
  InstanceOperators,
  PersonalApp,
  VolumeOperationView,
  VolumeSummary,
} from "@/components/personal-apps/types";
import type { WidgetDefinition } from "@/components/home-widgets/types";

export class ComputeApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Machine-readable fields the server returned beside `error`. */
    public details: Record<string, unknown> = {},
  ) {
    // A server that answers with a non-string `message` (a zod error object,
    // say) used to reach the screen as "[object Object]" — a message that
    // tells somebody nothing except that something is broken. The code is
    // always a string and always says more than that.
    super(typeof message === "string" && message ? message : code);
  }
}

/**
 * How long a single control-plane request may take before it is aborted.
 *
 * This is NOT the overall Formation deadline: a hung GET must not pin the
 * polling loop forever. Aborting one request lets the caller either retry
 * until the overall deadline or surface a timeout — but never sit in
 * `building` with a pending fetch that no deadline check can reach.
 */
export const COMPUTE_REQUEST_TIMEOUT_MS = 30_000;

async function request<T>(
  path: string,
  options: {
    method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    body?: unknown;
    headers?: Record<string, string>;
    rawBody?: Blob;
    signal?: AbortSignal;
    timeoutMs?: number;
    /**
     * Browser cache policy. `no-store` everywhere by default — a control
     * plane's answers are about right now.
     *
     * `no-cache` is for the one GET that is read on every visit and is
     * expected to be unchanged most of the time: it still asks the server
     * every time, but lets the browser revalidate with a validator and take a
     * `304` instead of re-transferring the body. `no-store` cannot do that,
     * because nothing is stored to validate against.
     */
    cache?: RequestCache;
  } = {},
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? COMPUTE_REQUEST_TIMEOUT_MS;
  const userSignal = options.signal;
  if (userSignal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
  const controller = new AbortController();
  let timedOut = false;
  const onUserAbort = () => controller.abort();
  const startTimer = () =>
    setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
  let timer = startTimer();
  userSignal?.addEventListener("abort", onUserAbort, { once: true });
  try {
    const method = options.method ?? "GET";
    // A 401 on a read joins auth recovery and is replayed once. The request
    // timeout does not run while the person is signing in.
    const response = await fetchWithAuthRecovery(
      () =>
        fetch(
          `${accountApiBase()}${path}`,
          withConnectionAuth({
            method,
            headers: {
              Accept: "application/json",
              ...options.headers,
              ...(options.body === undefined
                ? {}
                : { "Content-Type": "application/json" }),
            },
            cache: options.cache ?? "no-store",
            body:
              options.rawBody ??
              (options.body === undefined
                ? undefined
                : JSON.stringify(options.body)),
            signal: controller.signal,
          }),
        ),
      method,
      () => {
        clearTimeout(timer);
        return () => {
          timer = startTimer();
        };
      },
    );
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    } & Record<string, unknown>;
    if (!response.ok) {
      const { error: _error, message: _message, ...details } = payload;
      throw new ComputeApiError(
        response.status,
        payload.error ?? `http_${response.status}`,
        payload.message ?? "",
        details,
      );
    }
    return payload as T;
  } catch (cause) {
    // User cancellation wins over everything: callers turn this into an idle
    // UI rather than an error line.
    if (userSignal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    if (cause instanceof DOMException && cause.name === "AbortError") {
      if (timedOut) {
        throw new ComputeApiError(
          0,
          "request_timeout",
          "The request timed out.",
        );
      }
      throw cause;
    }
    throw cause;
  } finally {
    clearTimeout(timer);
    userSignal?.removeEventListener("abort", onUserAbort);
  }
}

/**
 * The URL for an icon the server described as a path.
 *
 * The API returns a PATH rather than a URL so the same response is correct
 * against staging and production without the server knowing which client is
 * asking. One function does the join, so no component has to know where the
 * API lives.
 */
export function iconImageUrl(path: string | null): string | null {
  return path ? `${accountApiBase()}${path}` : null;
}

/**
 * Make an older API's answer look like the current one, here at the boundary.
 *
 * `lanes[]` and `DataItem.lane` arrived with the multi-lane preview. An API
 * rolled back below that — or a cached response from one — would otherwise
 * reach UI that calls `preview.lanes.filter(...)` and throw, turning a
 * survivable version skew into a blank screen. Normalising once here keeps
 * every consumer written against one shape instead of making the whole UI
 * defensive about a field that is supposed to exist.
 *
 * The synthesised lane is the legacy answer stated honestly: one lane, the
 * one the old API knew about, carrying exactly the numbers it reported.
 */
export function normalizeDataPreview(preview: DataPreview): DataPreview {
  const items = (preview.items ?? []).map((item) =>
    item.lane ? item : { ...item, lane: preview.storage?.kind ?? "none" },
  );
  if (Array.isArray(preview.lanes)) return { ...preview, items };
  return {
    ...preview,
    items,
    lanes: [
      {
        kind: preview.storage?.kind ?? "none",
        protocol: preview.storage?.protocol ?? "",
        revision: preview.revision ?? 0,
        updated_at: preview.updated_at ?? null,
        item_count: preview.item_count ?? 0,
        total_bytes: preview.total_bytes ?? 0,
        ...(preview.unavailable_reason
          ? { unavailable_reason: preview.unavailable_reason }
          : {}),
      },
    ],
  };
}

export const computeClient = {
  runtimeEvidence(instanceId: string, options: { signal?: AbortSignal } = {}) {
    return request<{ evidence: { instance_id: string; route: { run_id: string | null } | null } }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/runtime-evidence`, options,
    );
  },
  runContractReceipt(instanceId: string, runId: string, options: { signal?: AbortSignal } = {}) {
    return request<{
      run_id: string;
      status: "verified" | "unverified" | "pending" | "unknown" | "not_available";
      receipt: { fully_satisfied: boolean; execution: { run_id: string }; observations: Array<{ id: string; outcome: string }> } | null;
    }>(`/v1/compute-instances/${encodeURIComponent(instanceId)}/runs/${encodeURIComponent(runId)}/contract-receipt`, options);
  },
  createSourceArchive(
    source: { url: string; ref?: string; subdirectory: string },
    options: { signal?: AbortSignal; idempotencyKey: string },
  ) {
    return request<SourceArchiveCreated>("/v1/computes/source-archives", {
      method: "POST", body: { source: { kind: "github", ...source } },
      headers: { "Idempotency-Key": options.idempotencyKey }, signal: options.signal,
    });
  },
  sourceArchiveAdmission(jobId: string, options: { signal?: AbortSignal } = {}) {
    return request<SourceArchiveAdmission>(`/v1/computes/source-archives/${encodeURIComponent(jobId)}/admission`, {
      signal: options.signal,
    });
  },

  prepareHostedExploration(body: {source_archive_job_id:string;binding_digest:string;path:string}, options:{signal?:AbortSignal;idempotencyKey:string}) {
    return request<unknown>("/v1/runtime-network/hosted-exploration-preparations",{method:"POST",body,headers:{"Idempotency-Key":options.idempotencyKey},signal:options.signal});
  },
  hostedPreparation(id:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration-preparations/${encodeURIComponent(id)}`,options);
  },
  cancelHostedPreparation(id:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration-preparations/${encodeURIComponent(id)}/cancel`,{method:"POST",signal:options.signal});
  },
  startHostedPreparation(id:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration-preparations/${encodeURIComponent(id)}/start`,{method:"POST",signal:options.signal});
  },
  hostedExplorationPreview(search:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration/${encodeURIComponent(search)}/preview`,options);
  },
  confirmHostedExploration(search:string,body:{satisfy_id:string;preview_ref:string},options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration/${encodeURIComponent(search)}/confirm`,{method:"POST",body,signal:options.signal});
  },
  cancelHostedExploration(search:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration/${encodeURIComponent(search)}/cancel`,{method:"POST",signal:options.signal});
  },
  hostedExplorationStatus(satisfy:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/satisfy/${encodeURIComponent(satisfy)}`,options);
  },

  queueHostedPromotion(search:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration/${encodeURIComponent(search)}/promotion`,{method:"POST",signal:options.signal});
  },
  hostedPromotionStatus(search:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration/${encodeURIComponent(search)}/promotion`,options);
  },
  completeHostedPromotion(search:string,options:{signal?:AbortSignal}={}) {
    return request<unknown>(`/v1/runtime-network/hosted-exploration/${encodeURIComponent(search)}/promotion/complete`,{method:"POST",signal:options.signal});
  },

  transferFormationSource(pointer: import("./ownerRuntimeRequest").FormationSourcePointer, options: { signal?: AbortSignal } = {}) {
    return request<import("./ownerRuntimeRequest").RuntimeSourceTransfer>("/v1/runtime-network/sources/from-formation", {
      method: "POST", body: pointer, signal: options.signal,
    });
  },
  satisfyFormationSource(pointer: import("./ownerRuntimeRequest").FormationSourcePointer, body: Record<string, unknown>, options: { signal?: AbortSignal } = {}) {
    const query = new URLSearchParams({ ...pointer });
    return request<import("./ownerRuntimeRequest").RuntimeRequestCreated>(`/v1/runtime-network/satisfy?${query}`, {
      method: "POST", body, signal: options.signal,
    });
  },

  /**
   * Turns a finished authoring submission into an App. Only the submission id
   * is sent: which artifact backs the App is the server's to decide.
   */
  createFromAuthoring(input: { submissionId: string; displayName?: string }, options:{signal?:AbortSignal;idempotencyKey?:string}={}) {
    return request<{ instance: PersonalApp }>("/v1/computes/from-authoring", {
      signal:options.signal,
      headers: options.idempotencyKey ? {"Idempotency-Key":options.idempotencyKey} : {},
      method: "POST",
      body: {
        submission_id: input.submissionId,
        ...(input.displayName ? { display_name: input.displayName } : {}),
      },
    });
  },

  /**
   * Hands a finished upload to Formation and returns what to poll.
   *
   * The upload id is all that is sent: the archive digest, the preset and the
   * build plan are read from the upload the server already holds. A client
   * that could name its own digest could point a build at other bytes.
   */
  formFromUpload(
    input: { uploadId: string; displayName?: string },
    options: { signal?: AbortSignal; timeoutMs?: number } = {},
  ) {
    return request<{ compute_id: string; formation_job_id: string }>(
      "/v1/computes/from-upload",
      {
        method: "POST",
        body: {
          upload_id: input.uploadId,
          ...(input.displayName ? { display_name: input.displayName } : {}),
        },
        signal: options.signal,
        timeoutMs: options.timeoutMs,
      },
    );
  },

  uploadSourceZip(archive: Blob, options: { signal?: AbortSignal; idempotencyKey?: string } = {}) {
    return request<{ upload_id: string; expires_at: number }>(
      "/v1/computes/source-uploads/zip",
      {
        method: "POST",
        rawBody: archive,
        headers: { "Content-Type": "application/zip", ...(options.idempotencyKey ? {"Idempotency-Key":options.idempotencyKey} : {}) },
        signal: options.signal,
      },
    );
  },

  formFromSource(
    input: {
      source: { kind: "zip"; upload_id: string } | ApprovedGithubArchiveSource;
      metadata: { title: string };
      admission_mode?: "private_isolated";
    },
    options: { signal?: AbortSignal; idempotencyKey: string },
  ) {
    return request<{ compute_id: string; formation_job_id: string }>(
      "/v1/computes/from-source",
      {
        method: "POST",
        body: input,
        signal: options.signal,
        headers: { "Idempotency-Key": options.idempotencyKey },
      },
    );
  },

  /**
   * A new version of an App that already exists, from the SAME canonical
   * source lane Add App uses.
   *
   * Update used to go through the Submission Wizard's authoring intake
   * (`/v1/authoring-sources/local/uploads`), which is a different feature with
   * its own production gate — so Update was dead in production while Add
   * worked, and staging could not see it because the gate is open there. The
   * two flows now differ only in whether they name a Compute to update.
   *
   * `from_schema_id` is the version the caller believes the App is on. The
   * server refuses a stale one rather than building on top of somebody else's
   * update.
   */
  updateFromSource(
    input: {
      computeId: string;
      fromSchemaId: string;
      source: { kind: "zip"; upload_id: string };
    },
    options: { signal?: AbortSignal; idempotencyKey: string },
  ) {
    return request<{ compute_id: string; formation_job_id: string }>(
      `/v1/computes/${encodeURIComponent(input.computeId)}/update-from-source`,
      {
        method: "POST",
        // No `metadata`: an update replaces the software, not what the App is
        // called. Omitting it inherits the current presentation.
        body: { source: input.source, from_schema_id: input.fromSchemaId },
        signal: options.signal,
        headers: { "Idempotency-Key": options.idempotencyKey },
      },
    );
  },

  retryFormationGpu(
    jobId: string,
    placement: { kind: "managed"; compute_account: "owner" | "operator" },
    options: { signal?: AbortSignal } = {},
  ) {
    return request<{ ok: true; job_id: string; status: string }>(
      `/v1/computes/formation-jobs/${encodeURIComponent(jobId)}/gpu-verification`,
      { method: "POST", body: { placement }, signal: options.signal },
    );
  },
  cancelFormationGpu(jobId: string) {
    return request<{ ok: true }>(
      `/v1/computes/formation-jobs/${encodeURIComponent(jobId)}/gpu-verification/cancel`,
      { method: "POST", timeoutMs: 10_000 },
    );
  },

  formationJob(
    jobId: string,
    options: { signal?: AbortSignal; timeoutMs?: number } = {},
  ) {
    return request<FormationJobSnapshot>(`/v1/computes/formation-jobs/${encodeURIComponent(jobId)}`, {
      signal: options.signal,
      timeoutMs: options.timeoutMs,
    });
  },

  /** Opens an App on a schema Formation has accepted. */
  openInstance(
    input: { computeId: string; computeSchemaId: string },
    options: { signal?: AbortSignal; timeoutMs?: number; idempotencyKey?: string } = {},
  ) {
    return request<{ instance: PersonalApp }>(
      `/v1/computes/${encodeURIComponent(input.computeId)}/instances`,
      {
        method: "POST",
        body: { compute_schema_id: input.computeSchemaId },
        headers: options.idempotencyKey ? {"Idempotency-Key":options.idempotencyKey} : {},
        signal: options.signal,
        timeoutMs: options.timeoutMs,
      },
    );
  },

  /**
   * What I call this App, and what its icon looks like to me.
   *
   * Sent per field: `null` clears an override, an absent field leaves it
   * alone. Addressed to the COMPUTE because the home screen shows one tile per
   * App — a name stored per data set would make the tile's label depend on
   * which set happened to sort first.
   */
  personalize(
    computeId: string,
    patch: {
      display_name?: string | null;
      icon_color?: string | null;
      icon_glyph?: string | null;
    },
  ) {
    return request<{
      compute_id: string;
      display_name: string | null;
      icon_color: string | null;
      icon_glyph: string | null;
    }>(`/v1/computes/${encodeURIComponent(computeId)}/personalization`, {
      method: "PATCH",
      body: patch,
    });
  },

  /** Uploads the raw bytes. The server sniffs what they are; it does not ask. */
  async uploadIcon(computeId: string, file: File) {
    const response = await fetchWithConnectionAuth(
      `${accountApiBase()}/v1/computes/${encodeURIComponent(computeId)}/icon`,
      {
        method: "PUT",
        headers: { Accept: "application/json" },
        cache: "no-store",
        body: file,
      },
    );
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
      icon_image_path?: string;
    };
    if (!response.ok) {
      throw new ComputeApiError(
        response.status,
        payload.error ?? `http_${response.status}`,
        "",
      );
    }
    return payload as { compute_id: string; icon_image_path: string };
  },

  removeIcon(computeId: string) {
    return request<{ compute_id: string; icon_image_path: null }>(
      `/v1/computes/${encodeURIComponent(computeId)}/icon`,
      { method: "DELETE" },
    );
  },

  /**
   * My Apps' listing.
   *
   * `no-cache` rather than `no-store`: this is re-read on every visit and on
   * every revalidation signal, and it is usually identical to the last answer.
   * Allowing the browser to hold it and revalidate means an unchanged listing
   * costs a conditional request instead of the whole body — once the endpoint
   * answers with a validator and `Cache-Control: private, no-cache`, which is
   * API-side work. Until then this behaves exactly as `no-store` did.
   */
  list() {
    return request<{ instances: PersonalApp[] }>("/v1/compute-instances", {
      cache: "no-cache",
    });
  },

  /**
   * Who is in each of my Apps right now.
   *
   * Separate from `list()` on purpose: the listing is stable and presence is
   * not, so polling one must not mean re-fetching the other.
   */
  presence() {
    return request<{ instances: InstancePresence[] }>(
      "/v1/compute-instances/presence",
    );
  },

  operators() {
    return request<{ instances: InstanceOperators[] }>(
      "/v1/compute-instances/operators",
    );
  },

  /**
   * One App, and what Discover says about it.
   *
   * `publication` rides along rather than coming from a second call: App
   * Detail must not render its publish action before it knows whether this App
   * is already on Discover, and a second round trip would make that flicker.
   * Null for every viewer who is not the publisher.
   */
  /**
   * What starting this App on one boundary would mean right now. Read-only:
   * nothing is allocated, and the quote expires quickly.
   */
  executionOffer(instanceId: string, provider: ExecutionProvider) {
    return request<{ offer: ExecutionOffer }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/execution-offer?provider=${provider}`,
    );
  },

  /**
   * Confirm where this App runs, against the quote the owner was shown. The
   * server recomputes the quote and refuses a stale one; every later start of
   * the App uses this decision and never picks another boundary by itself.
   */
  confirmExecutionPlacement(
    instanceId: string,
    input: { provider: ExecutionProvider; quoteId: string },
  ) {
    return request<{ execution_placement: ExecutionPlacement }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/execution-placement`,
      {
        method: "PUT",
        body: { provider: input.provider, quote_id: input.quoteId },
      },
    );
  },

  get(instanceId: string, signal?: AbortSignal) {
    return request<{
      instance: PersonalApp;
      public_try_cleanup?: { resources_released: boolean } | null;
      publication?: DiscoverPublication | null;
      can_use_private_source?: boolean;
      can_manage_source?: boolean;
      /** The current revision's Home surfaces. Absent on an older API. */
      widgets?: WidgetDefinition[];
    }>(`/v1/compute-instances/${encodeURIComponent(instanceId)}`, { signal }).then(
      (response) => ({
        public_try_cleanup: response.public_try_cleanup,
        instance: {
          ...response.instance,
          discover_app_id: response.instance.discover_app_id ?? null,
        },
        // An API older than the source-visibility feature omits the field;
        // `open` is the correct pre-feature default.
        publication: response.publication
          ? {
              ...response.publication,
              source_visibility:
                response.publication.source_visibility ?? "open",
            }
          : response.publication,
        can_use_private_source: response.can_use_private_source === true,
        // Source authority is the SOURCE's Compute owner, never the Instance
        // owner — an Added App has a user-owned Instance over somebody else's
        // Compute. An older API cannot answer it, so fail closed.
        can_manage_source: response.can_manage_source === true,
        // An Application without a presentation descriptor declares no
        // Widgets; an older API answers none at all.
        widgets: Array.isArray(response.widgets) ? response.widgets : [],
      }),
    );
  },

  stop(instanceId: string) {
    return request<{
      status: "stopping" | "sleeping";
      public_try: boolean;
      public_try_cleanup?: { resources_released: boolean } | null;
    }>(`/v1/compute-instances/${encodeURIComponent(instanceId)}/stop`, {
      method: "POST",
    });
  },

  setRuntimePolicy(
    instanceId: string,
    input: {
      mode: "on_demand" | "always_on";
      desired_state: "running" | "stopped";
    },
  ) {
    return request<{ runtime_policy: PersonalApp["runtime_policy"] }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/runtime-policy`,
      { method: "PATCH", body: input },
    );
  },

  /** How this App's source should be shown or handed out. Owner-scoped. */
  getSource(instanceId: string) {
    return request<AppSourcePresentation>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/source`,
    );
  },

  /** The PUBLIC source of a Discover listing. Used for an App that was Added,
   *  whose source the viewer does not own. Private answers with only
   *  `{ visibility: "private" }`. */
  getPublicSource(discoverAppId: string) {
    return request<AppSourcePresentation>(
      `/v1/discover/apps/${encodeURIComponent(discoverAppId)}/source`,
    );
  },

  remove(instanceId: string) {
    return request<{ status: "deleted" }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}`,
      { method: "DELETE" },
    );
  },

  /**
   * Removes MY App: every instance I own of this Compute, with all of their
   * saved data. The Compute, its software and shared artifacts stay — this
   * only ends my App, which is what My Apps shows as one tile.
   */
  removeApp(computeId: string) {
    return request<{ status: "deleted"; deleted_instance_ids: string[] }>(
      `/v1/computes/${encodeURIComponent(computeId)}/instances`,
      { method: "DELETE" },
    );
  },

  /**
   * New software for a Compute that already exists.
   *
   * Deliberately NOT `formFromUpload` with an extra field: that one makes an
   * App, this one replaces the software of one somebody already has, and the
   * server asks a different question about who may do it. Nothing is applied
   * by this call — it starts a build.
   */
  updateFromUpload(
    input: {
      computeId: string;
      uploadId: string;
      fromSchemaId: string;
    },
    options: { signal?: AbortSignal; timeoutMs?: number } = {},
  ) {
    return request<{ compute_id: string; formation_job_id: string }>(
      `/v1/computes/${encodeURIComponent(input.computeId)}/update-from-upload`,
      {
        method: "POST",
        body: {
          upload_id: input.uploadId,
          from_schema_id: input.fromSchemaId,
        },
        signal: options.signal,
        timeoutMs: options.timeoutMs,
      },
    );
  },

  /** Can the candidate carry this App's saved data across? Asked before the
   *  old software is disturbed, so "no" costs nothing. */
  publishedUpdate(instanceId:string,signal?:AbortSignal) {
    return request<{update:import("./PublishedAppUpdateControl").PublishedAppUpdate|null}>(`/v1/compute-instances/${encodeURIComponent(instanceId)}/published-update`,{signal});
  },

  schemaCompatibility(
    instanceId: string,
    toSchemaId: string,
    options: { signal?: AbortSignal; timeoutMs?: number } = {},
  ) {
    return request<{
      compatible: boolean;
      from_schema_id: string;
      to_schema_id: string;
      reason: string | null;
      /** False when this version could not start if adopted. Absent from an
       *  older API, which never refused on this. */
      runnable?: boolean;
      blocked_reason?: string | null;
    }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/schema-compatibility?to_schema_id=${encodeURIComponent(toSchemaId)}`,
      { signal: options.signal, timeoutMs: options.timeoutMs },
    );
  },

  /**
   * Adopt a candidate.
   *
   * `expectedFromSchemaId` is what makes this safe to confirm slowly: if
   * another device updated the App while this person was reading the
   * confirmation, the server refuses rather than taking the App backwards.
   */
  applySchema(input: {
    instanceId: string;
    toSchemaId: string;
    expectedFromSchemaId: string;
    publication?: {id:string;version:number};
  }) {
    return request<{ status: string }>(
      `/v1/compute-instances/${encodeURIComponent(input.instanceId)}/schema`,
      {
        method: "POST",
        body: {
          to_schema_id: input.toSchemaId,
          expected_from_schema_id: input.expectedFromSchemaId,
          ...(input.publication ? {publication:input.publication} : {}),
        },
      },
    );
  },

  /**
   * What this App has saved, described as data rather than as storage.
   *
   * A preview: values truncate and the list caps, while the counts describe
   * the whole thing.
   */
  async readData(instanceId: string) {
    const response = await request<{
      data: DataPreview;
      volume?: VolumeSummary | null;
    }>(`/v1/compute-instances/${encodeURIComponent(instanceId)}/data`);
    return {
      ...response,
      data: normalizeDataPreview(response.data),
      volume: response.volume ?? null,
    };
  },

  /**
   * Clears the App's data. The App itself, and its URL, are untouched.
   *
   * For an App whose data lives in a volume on its Runner this starts the
   * volume's deletion instead, answered with the operation to follow.
   */
  resetState(instanceId: string) {
    return request<
      | { status: "reset"; revision: number }
      | { status: "deleting"; operation: VolumeOperationView }
    >(`/v1/compute-instances/${encodeURIComponent(instanceId)}/reset-state`, {
      method: "POST",
    });
  },

  /** Saves the volume's current contents as a checkpoint. */
  createCheckpoint(instanceId: string) {
    return request<{ operation: VolumeOperationView }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/state/checkpoints`,
      { method: "POST" },
    );
  },

  /** Replaces the volume's contents with a checkpoint. */
  restoreCheckpoint(instanceId: string, revisionId: string) {
    return request<{ operation: VolumeOperationView }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/state/restore`,
      { method: "POST", body: { revision_id: revisionId } },
    );
  },

  /** Re-enables launch/network admission for one reviewed restore generation. */
  releaseRestoreHold(instanceId: string, operationId: string, generation: number) {
    return request<{
      recovery_hold: { generation: number; status: "released" };
    }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/state/operations/${encodeURIComponent(operationId)}/release-restore-hold`,
      { method: "POST", body: { generation } },
    );
  },

  /** Reads (and so advances) a volume operation. */
  readVolumeOperation(instanceId: string, operationId: string) {
    return request<{ operation: VolumeOperationView }>(
      `/v1/compute-instances/${encodeURIComponent(instanceId)}/state/operations/${encodeURIComponent(operationId)}`,
    );
  },
};

/**
 * Download an App's verified source through the URL the server handed back.
 *
 * The URL is digest-addressed (`schema_id + source_digest`), so it may be
 * cached hard by the browser; it must NOT be rebuilt client-side. The server
 * refuses a URL whose Schema or digest no longer matches, which is what keeps a
 * stale bookmark from downloading a newer revision's bytes.
 */
export async function downloadAppSource(
  presentation: DownloadSourcePresentation,
): Promise<void> {
  if (!presentation.download_url) {
    throw new ComputeApiError(400, "source_download_unavailable", "");
  }
  const response = await fetchWithConnectionAuth(
    `${accountApiBase()}${presentation.download_url}`,
    { cache: "no-store" },
  );
  if (!response.ok) {
    throw new ComputeApiError(response.status, `http_${response.status}`, "");
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = presentation.filename ?? "source";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // The same inline revoke-on-next-tick every other download path uses; 60s is
  // generous slack for a slow click-to-save dialog.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

// Shared authenticated control-plane transport, including read-only auth recovery.
export { request as computeRequest };
